import type { TokenSource } from "@its-engineering-and-development/rbac-react";
import {
  authAccessTokenChangedEvent,
  authAccessTokenKey,
  authRefreshTokenKey,
  authTokenStore,
} from "@app/lib/auth/token-store";

type AccessTokenChangedDetail = {
  token: string | null;
};

function normalizeToken(token: string | null): string | null {
  if (!token) {
    return null;
  }

  const trimmed = token.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function createRbacTokenSource(): TokenSource {
  let lastKnownToken = normalizeToken(authTokenStore.getAccessToken());

  const hasRefreshToken = () =>
    normalizeToken(authTokenStore.getRefreshToken()) !== null;

  const getVisibleToken = (nextToken?: string | null) => {
    const resolvedToken =
      nextToken === undefined
        ? normalizeToken(authTokenStore.getAccessToken())
        : nextToken;

    if (resolvedToken) {
      lastKnownToken = resolvedToken;
      return resolvedToken;
    }

    if (hasRefreshToken()) {
      return lastKnownToken;
    }

    lastKnownToken = null;
    return null;
  };

  return {
    getAccessToken: () => getVisibleToken(),
    onTokenChange: (callback) => {
      const handleAccessTokenChanged = (event: Event) => {
        const customEvent = event as CustomEvent<AccessTokenChangedDetail>;
        callback(
          getVisibleToken(normalizeToken(customEvent.detail?.token ?? null)),
        );
      };

      const handleStorage = (event: StorageEvent) => {
        if (
          event.key !== authAccessTokenKey &&
          event.key !== authRefreshTokenKey
        ) {
          return;
        }

        const nextToken =
          event.key === authAccessTokenKey
            ? normalizeToken(event.newValue)
            : undefined;

        callback(getVisibleToken(nextToken));
      };

      window.addEventListener(
        authAccessTokenChangedEvent,
        handleAccessTokenChanged,
      );
      window.addEventListener("storage", handleStorage);

      return () => {
        window.removeEventListener(
          authAccessTokenChangedEvent,
          handleAccessTokenChanged,
        );
        window.removeEventListener("storage", handleStorage);
      };
    },
  };
}
